import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, PiggyBank, TrendingUp, Calculator, Cake, Landmark, Scale, Receipt, Fuel, Coins, Percent, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { CalcNotePromo } from '@/components/CalcNotePromo';
import { SearchTriggerButton } from '@/components/CalculatorSearchDialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DrawerClose,
} from '@/components/ui/drawer';

const calculatorCategories = [
  {
    category: 'Loans',
    items: [
      { title: 'EMI Calculator', url: '/', icon: PiggyBank, description: 'Calculate monthly loan payments' },
    ],
  },
  {
    category: 'Investments',
    items: [
      { title: 'SIP Calculator', url: '/sip-calculator', icon: TrendingUp, description: 'Plan systematic investments' },
      { title: 'FD Calculator', url: '/fd-calculator', icon: Landmark, description: 'Calculate fixed deposit returns' },
      { title: 'Compound Interest', url: '/compound-interest-calculator', icon: Coins, description: 'Calculate compound growth' },
    ],
  },
  {
    category: 'Utilities',
    items: [
      { title: 'General Calculator', url: '/calculator', icon: Calculator, description: 'Quick math & scientific operations' },
      { title: 'GST Calculator', url: '/gst-calculator', icon: Receipt, description: 'Calculate GST, CGST, SGST & IGST' },
      { title: 'Percentage Calculator', url: '/percentage-calculator', icon: Percent, description: 'Calculate percent increase & discount' },
      { title: 'Mileage Calculator', url: '/mileage-calculator', icon: Fuel, description: 'Calculate fuel efficiency & trip cost' },
    ],
  },
  {
    category: 'Others',
    items: [
      { title: 'Age Calculator', url: '/age-calculator', icon: Cake, description: 'Calculate age & birthday countdown' },
      { title: 'BMI Calculator', url: '/bmi-calculator', icon: Scale, description: 'Calculate body mass index' },
    ],
  },
];

export function MobileBottomSheet() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const isActive = (url: string) => location.pathname === url;

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 rounded-xl hover:bg-primary/10 active:scale-95 transition-transform"
          aria-label="Open menu"
        >
          <Menu className="h-7 w-7" strokeWidth={2.5} />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[88vh]">
        <DrawerHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <DrawerTitle className="text-lg font-bold">Calculators</DrawerTitle>
            <p className="text-xs text-muted-foreground">Universal calculators by Utiloom</p>
          </div>
          <DrawerClose asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Close menu">
              <X className="h-4 w-4" />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        
        <div className="overflow-y-auto px-4 pb-8 pt-2 space-y-5">
          {/* Quick Search */}
          <SearchTriggerButton />

          {/* Mobile CalcNote App Promo Card */}
          <CalcNotePromo variant="mobile" />

          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-5"
            >
              {calculatorCategories.map((category, catIndex) => (
                <motion.div
                  key={category.category}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: catIndex * 0.05 }}
                >
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
                    {category.category}
                  </h3>
                  <div className="space-y-1">
                    {category.items.map((item) => (
                      <Link
                        key={item.url}
                        to={item.url}
                        onClick={() => setOpen(false)}
                        className={`flex items-center gap-3 w-full p-3 rounded-xl transition-all ${
                          isActive(item.url)
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-muted/50'
                        }`}
                      >
                        <div className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors shrink-0 ${
                          isActive(item.url)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted/50 text-muted-foreground'
                        }`}>
                          <item.icon className="h-5 w-5" />
                        </div>
                        <div className="text-left min-w-0 flex-1">
                          <p className={`font-medium text-sm truncate ${isActive(item.url) ? 'text-primary' : 'text-foreground'}`}>
                            {item.title}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
